import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Form, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import Swal from "sweetalert2";
import { useCurrency } from "../../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../../Hooks/useDisableScrollOnFocus";
import { toMoneyNumber } from "../../../utils/financeEngine";
import { convertBillingAmount } from "../../PharmacySales/NewSales/utils/billingUtils";
import useCollectPaymentBilling from "../hooks/useCollectPaymentBilling";
import { formatAmount, formatDateList, getCollectPaymentAppointmentId, getPatientAccountPatientId, getPatientDisplayName, formatBillingForPayRemainingSales, PAYMENT_METHOD_OPTIONS, validatePayRemainingNewSalePayload } from "../utils/patientAccountUtils";
import { toast } from "react-toastify";
import CollectPaymentBillingPanel from "./CollectPaymentBillingPanel";
import CollectPaymentTreatmentPanel from "./CollectPaymentTreatmentPanel";

const PAYMENT_TOLERANCE = 0.01;

const resolveCollectPaymentStatus = (received, amountDue) => {
	if ((Number(received) || 0) <= 0) return { status: "unpaid", statusLabel: "Unpaid" };
	if (Number(received) >= Number(amountDue) - PAYMENT_TOLERANCE) {
		return { status: "paid", statusLabel: "Paid" };
	}
	return { status: "partial_paid", statusLabel: "Partial Paid" };
};

const CollectPaymentModal = ({
	isOpen,
	toggle,
	invoice,
	onSubmit,
	isSubmitting,
}) => {
	const { t } = useTranslation();
	const { allowedCurrencies, getCurrencySymbol, convertCurrency, refreshRates } = useCurrency();
	const disableScrollProps = useDisableScrollOnFocus();

	const [formValues, setFormValues] = useState({
		currency: "",
		paymentMethod: "cash",
		amount: "",
	});
	const [formErrors, setFormErrors] = useState({});
	const [formSubmitted, setFormSubmitted] = useState(false);
	const amountPrefilledRef = useRef(false);
	const prevDueAmountRef = useRef(null);

	const billing = useCollectPaymentBilling({
		invoice,
		isOpen,
		paymentCurrency: formValues.currency || undefined,
	});

	const {
		loading,
		normalizedInvoice,
		baseCurrency,
		displayCurrency,
		paymentCurrency: billingPaymentCurrency,
		defaultCurrency,
		dueAmount,
		dueAmountPayment,
		paidSoFar,
		grandTotal,
		isTreatmentPackage,
		packagePrice,
		billingSummary,
		buildCollectPaymentPayload,
	} = billing;

	const additionalServicesTotal = useMemo(() => {
		if (!isTreatmentPackage) return 0;
		// Prefer tax/discount-adjusted services total when available.
		return toMoneyNumber(
			billingSummary?.services_total ??
				(billingSummary?.consultation_total ?? 0) +
					(billingSummary?.additional_services_total ?? 0)
		);
	}, [
		billingSummary?.additional_services_total,
		billingSummary?.consultation_total,
		billingSummary?.services_total,
		isTreatmentPackage,
	]);

	const paymentCurrency = formValues.currency || billingPaymentCurrency || baseCurrency;

	const paymentBalance = useMemo(() => {
		const amountDuePayment = toMoneyNumber(dueAmountPayment);
		const amountDueBase = toMoneyNumber(dueAmount);
		const received = Number(formValues.amount) || 0;
		const pending = Math.max(0, toMoneyNumber(amountDuePayment - received));
		const change = Math.max(0, toMoneyNumber(received - amountDuePayment));
		const { status, statusLabel } = resolveCollectPaymentStatus(received, amountDuePayment);

		const receivedBase =
			paymentCurrency === baseCurrency
				? received
				: convertBillingAmount(
						received,
						paymentCurrency,
						baseCurrency,
						convertCurrency,
						defaultCurrency,
						allowedCurrencies
					);
		const pendingBase = Math.max(0, toMoneyNumber(amountDueBase - receivedBase));

		return {
			amountDuePayment,
			amountDueBase,
			received,
			pending,
			pendingBase,
			change,
			status,
			statusLabel,
			showBaseCurrency: paymentCurrency !== baseCurrency,
			isPartial:
				received > 0 &&
				received < amountDuePayment - PAYMENT_TOLERANCE &&
				amountDuePayment > 0,
		};
	}, [
		allowedCurrencies,
		baseCurrency,
		convertCurrency,
		defaultCurrency,
		dueAmount,
		dueAmountPayment,
		formValues.amount,
		paymentCurrency,
	]);

	const initialCurrency = baseCurrency || allowedCurrencies?.[0] || "";

	useEffect(() => {
		if (!isOpen || !normalizedInvoice) return;
		setFormValues({
			currency: initialCurrency,
			paymentMethod: "cash",
			amount: "",
		});
		setFormErrors({});
		setFormSubmitted(false);
		amountPrefilledRef.current = false;
		prevDueAmountRef.current = null;
	}, [isOpen, normalizedInvoice, initialCurrency]);

	useEffect(() => {
		if (!isOpen || loading) return;

		const targetDue = toMoneyNumber(dueAmountPayment);

		setFormValues((prev) => {
			const received = toMoneyNumber(prev.amount);
			const prevTarget = prevDueAmountRef.current;

			if (!amountPrefilledRef.current && targetDue > 0) {
				amountPrefilledRef.current = true;
				prevDueAmountRef.current = targetDue;
				return { ...prev, amount: String(targetDue) };
			}

			if (
				amountPrefilledRef.current &&
				prevTarget !== null &&
				received === toMoneyNumber(prevTarget) &&
				targetDue !== prevTarget
			) {
				prevDueAmountRef.current = targetDue;
				return { ...prev, amount: targetDue > 0 ? String(targetDue) : "" };
			}

			prevDueAmountRef.current = targetDue;
			return prev;
		});
	}, [dueAmountPayment, isOpen, loading]);

	const handleInputChange = async (e) => {
		const { name, value } = e.target;

		if (name === "currency") {
			await refreshRates();

			setFormValues((prev) => {
				const next = { ...prev, currency: value };
				const currentAmount = Number(prev.amount) || 0;
				if (currentAmount > 0 && prev.currency && prev.currency !== value) {
					next.amount = String(
						convertBillingAmount(
							currentAmount,
							prev.currency,
							value,
							convertCurrency,
							defaultCurrency,
							allowedCurrencies
						)
					);
				}
				return next;
			});
			setFormErrors((prev) => ({ ...prev, currency: "" }));
			return;
		}

		setFormValues((prev) => ({ ...prev, [name]: value }));
		setFormErrors((prev) => ({ ...prev, [name]: "" }));
	};

	const handleAmountChange = (e) => {
		const { value } = e.target;
		if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
			setFormValues((prev) => ({ ...prev, amount: value }));
			setFormErrors((prev) => ({ ...prev, amount: "" }));
		}
	};

	const validateForm = () => {
		const errors = {};
		const amount = Number(formValues.amount);

		if (!formValues.currency) errors.currency = t("Currency is required.");
		if (!formValues.paymentMethod) errors.paymentMethod = t("Payment method is required.");
		if (!formValues.amount && formValues.amount !== 0) {
			errors.amount = t("Amount is required.");
		} else if (Number.isNaN(amount) || amount <= 0) {
			errors.amount = t("Amount must be greater than zero.");
		} else if (amount > dueAmountPayment) {
			errors.amount = t("Amount cannot exceed pending balance.");
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const submitPayment = async (markAsPaid) => {
		setFormSubmitted(true);
		if (!validateForm()) return;

		const amount = Number(formValues.amount);
		if (amount < dueAmountPayment && !markAsPaid) {
			const confirm = await Swal.fire({
				title: t("Confirm Partial Payment"),
				text: t("Remaining balance will stay outstanding. Continue?"),
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: t("Continue"),
				cancelButtonText: t("Cancel"),
			});
			if (!confirm.isConfirmed) return;
		}

		const appointmentId = getCollectPaymentAppointmentId(invoice);
		const billingPayload = buildCollectPaymentPayload({
			markAsPaid,
			paidAmount: amount,
			currency: formValues.currency,
			paymentMode: formValues.paymentMethod,
		});

		const formattedBilling = formatBillingForPayRemainingSales(billingPayload, invoice, {
			currency: formValues.currency,
		});

		const validationErrors = validatePayRemainingNewSalePayload({
			appointmentId,
			paidAmount: amount,
			paymentType: formValues.paymentMethod,
			patientId: getPatientAccountPatientId(invoice),
			patientName: getPatientDisplayName(invoice?.patient),
			billing: formattedBilling,
			t,
		});

		if (Object.keys(validationErrors).length > 0) {
			toast.error(Object.values(validationErrors)[0]);
			return;
		}

		onSubmit({
			appointmentId,
			paidAmount: amount,
			currency: formValues.currency,
			paymentMode: formValues.paymentMethod,
			markAsPaid,
			isConversion: formValues.currency !== baseCurrency,
			baseCurrency,
			invoiceRecord: invoice,
			patientId: getPatientAccountPatientId(invoice),
			patientName: getPatientDisplayName(invoice?.patient),
			billing: formattedBilling,
		});
	};

	if (!invoice || !normalizedInvoice) return null;

	return (
		<Modal
			isOpen={isOpen}
			toggle={toggle}
			size="xl"
			centered
			scrollable
			backdrop="static"
			keyboard={false}
			className="collect-payment-modal"
			aria-labelledby="collect-payment-modal-title"
		>
			<ModalHeader toggle={toggle} id="collect-payment-modal-title" className="cp-modal-header">
				<div className="cp-modal-title">
					{t("Collect Payment")}
					{isTreatmentPackage && (
						<span className="badge tp-type-badge ms-2">{t("Treatment Package")}</span>
					)}
				</div>
				<small className="cp-modal-subtitle">{normalizedInvoice.invoice_no}</small>
			</ModalHeader>
			<ModalBody>
				<div className="cp-visit-banner cp-visit-banner--compact">
					<div className="cp-visit-banner-main">
						<div className="cp-visit-name">
							{invoice?.patient?.name || normalizedInvoice.patientName || "-"}
						</div>
						<div className="cp-visit-meta">
							<span>{formatDateList(normalizedInvoice.invoice_date)}</span>
							{invoice?.consultation_reason && (
								<>
									<span aria-hidden="true">·</span>
									<span>{invoice.consultation_reason}</span>
								</>
							)}
						</div>
					</div>
					<div className="cp-visit-doctor">
						<span className="cp-visit-doctor-label">{t("Doctor")}</span>
						<strong>{invoice?.doctor?.name || "-"}</strong>
					</div>
				</div>

				<Row className="g-2 cp-layout">
					<Col lg={8}>
						{isTreatmentPackage ? (
							<CollectPaymentTreatmentPanel invoice={invoice} {...billing} />
						) : (
							<CollectPaymentBillingPanel invoice={invoice} {...billing} />
						)}
						{isTreatmentPackage && (
							<div className="cp-tp-note" role="note">
								<i className="fa fa-info-circle me-1" aria-hidden="true" />
								{t("Consultation fee is not included for treatment package visits.")}
							</div>
						)}
					</Col>
					<Col lg={4}>
						<div className="cp-side-panel">
							<div className="cp-summary-card">
								<h6 className="cp-summary-title">{t("Payment Summary")}</h6>
								{isTreatmentPackage ? (
									<>
										<div className="cp-summary-row">
											<span>{t("Package Price")}</span>
											<strong>
												{formatAmount(packagePrice ?? 0, displayCurrency, getCurrencySymbol)}
											</strong>
										</div>
										{additionalServicesTotal > 0 && (
											<div className="cp-summary-row">
												<span>{t("Additional Services")}</span>
												<strong>
													{formatAmount(
														additionalServicesTotal,
														displayCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)}
									</>
								) : (
									<div className="cp-summary-row">
										<span>{t("Clinic Consultation & Services")}</span>
										<strong>
											{formatAmount(
												billingSummary?.services_total ?? 0,
												displayCurrency,
												getCurrencySymbol
											)}
										</strong>
									</div>
								)}
								{(billing.billingFlags?.showLabSection || isTreatmentPackage) && (
								<div className="cp-summary-row">
									<span>{t("Lab & Investigations")}</span>
									<strong>
										{formatAmount(
											billingSummary?.investigation_total ?? 0,
											displayCurrency,
											getCurrencySymbol
										)}
									</strong>
								</div>
								)}
								{(billingSummary?.pharmacy_total ?? 0) > 0 && (
									<div className="cp-summary-row">
										<span>{t("Pharmacy Purchases")}</span>
										<strong>
											{formatAmount(
												billingSummary?.pharmacy_total ?? 0,
												displayCurrency,
												getCurrencySymbol
											)}
										</strong>
									</div>
								)}
								<div className="cp-summary-row cp-summary-row--divider">
									<span>{t("Invoice Total")}</span>
									<strong>
										{formatAmount(grandTotal, displayCurrency, getCurrencySymbol)}
									</strong>
								</div>
								<div className="cp-summary-row cp-summary-row--paid">
									<span>{t("Paid")}</span>
									<strong>
										{formatAmount(paidSoFar, displayCurrency, getCurrencySymbol)}
									</strong>
								</div>
								<div className="cp-summary-row cp-summary-row--due">
									<span>{t("Balance Due")}</span>
									<strong>
										{formatAmount(dueAmount, displayCurrency, getCurrencySymbol)}
									</strong>
								</div>
							</div>

							<Form className="cp-payment-form" noValidate onSubmit={(e) => e.preventDefault()}>
								<h6 className="cp-summary-title mb-2">{t("Payment")}</h6>
								<div className="row g-2 mb-2">
									<Col lg={6}>
										<div>
											<Label for="collectCurrency" className="cp-form-label">
												{t("Currency")} <span className="text-danger">*</span>
											</Label>
											<select
												id="collectCurrency"
												name="currency"
												className="form-control form-select form-select-sm"
												value={formValues.currency}
												onChange={handleInputChange}
												{...disableScrollProps}
											>
												<option value="">{t("Select Currency")}</option>
												{(allowedCurrencies?.length ? allowedCurrencies : [initialCurrency]).map(
													(code) => (
														<option key={code} value={code}>
															({getCurrencySymbol(code)}) {code}
														</option>
													)
												)}
											</select>
											{formSubmitted && formErrors.currency && (
												<div className="text-danger small mt-1">{formErrors.currency}</div>
											)}
										</div>
									</Col>
									<Col lg={6}>
										<div>
											<Label for="paymentMethod" className="cp-form-label">
												{t("Payment Method")} <span className="text-danger">*</span>
											</Label>
											<select
												id="paymentMethod"
												name="paymentMethod"
												className="form-control form-select form-select-sm"
												value={formValues.paymentMethod}
												onChange={handleInputChange}
												{...disableScrollProps}
											>
												{PAYMENT_METHOD_OPTIONS.map((option) => (
													<option key={option.value} value={option.value}>
														{t(option.label)}
													</option>
												))}
											</select>
											{formSubmitted && formErrors.paymentMethod && (
												<div className="text-danger small mt-1">{formErrors.paymentMethod}</div>
											)}
										</div>
									</Col>
								</div>
								<div className="mb-2">
									<Label className="cp-form-label">{t("Balance Due")} ({paymentCurrency})</Label>
									<div className="cp-amount-due-display">
										{formatAmount(
											paymentBalance.amountDuePayment,
											paymentCurrency,
											getCurrencySymbol
										)}
									</div>
									{paymentBalance.showBaseCurrency && (
										<small className="cp-amount-due-base text-muted">
											{formatAmount(
												paymentBalance.amountDueBase,
												baseCurrency,
												getCurrencySymbol
											)}{" "}
											({baseCurrency})
										</small>
									)}
								</div>
								<div className="mb-0">
									<Label for="amountReceived" className="cp-form-label">
										{t("Amount to Collect")} ({paymentCurrency}){" "}
										<span className="text-danger">*</span>
									</Label>
									<input
										id="amountReceived"
										name="amount"
										type="text"
										inputMode="decimal"
										className="form-control form-control-sm"
										value={formValues.amount}
										onChange={handleAmountChange}
										placeholder={t("Enter amount")}
										{...disableScrollProps}
									/>
									{formSubmitted && formErrors.amount && (
										<div className="text-danger small mt-1">{formErrors.amount}</div>
									)}
								</div>

								{(paymentBalance.received > 0 || paymentBalance.amountDuePayment > 0) && (
									<div className="cp-balance-box mt-2">
										<div
											className={`cp-balance-row cp-balance-row--status cp-balance-row--${paymentBalance.status
												.toLowerCase()
												.replace(/[\s_]+/g, "-")}`}
										>
											<span>{t("Status")}</span>
											<strong>{t(paymentBalance.statusLabel)}</strong>
										</div>
										{/* {paymentBalance.received > 0 && (
											<div className="cp-balance-row cp-balance-row--collected">
												<span>{t("Collecting Now")} ({paymentCurrency})</span>
												<strong>
													{formatAmount(
														paymentBalance.received,
														paymentCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)} */}
										<div className="cp-balance-row cp-balance-row--pending">
											<span>{t("Remaining Balance")} ({paymentCurrency})</span>
											<strong>
												{formatAmount(
													paymentBalance.pending,
													paymentCurrency,
													getCurrencySymbol
												)}
											</strong>
										</div>
										{paymentBalance.showBaseCurrency && (
											<div className="cp-balance-row cp-balance-row--pending-alt">
												<span>{t("Remaining Balance")} ({baseCurrency})</span>
												<strong>
													{formatAmount(
														paymentBalance.pendingBase,
														baseCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)}
										{paymentBalance.change > 0 && (
											<div className="cp-balance-row cp-balance-row--change">
												<span>{t("Change Amount")} ({paymentCurrency})</span>
												<strong>
													{formatAmount(
														paymentBalance.change,
														paymentCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)}
									</div>
								)}

								{/* {paymentBalance.isPartial && (
									<div className="cp-partial-payment-hint" role="status">
										<i className="fa fa-exclamation-circle" aria-hidden="true" />
										<span>
											{t("Partial payment — {{amount}} will remain outstanding after this collection.", {
												amount: formatAmount(
													paymentBalance.pendingBase,
													baseCurrency,
													getCurrencySymbol
												),
											})}
										</span>
									</div>
								)} */}
							</Form>
						</div>
					</Col>
				</Row>
			</ModalBody>
			<ModalFooter className="cp-modal-footer">
				{/* {canMarkPaid && ( */}
					<button
						type="button"
						className="btn btn-success"
						onClick={() => submitPayment(true)}
						disabled={isSubmitting || loading || dueAmount <= 0}
					>
						{t("Collect & Mark As Paid")}
					</button>
				{/* )} */}
				<button
					type="button"
					className="btn btn-primary"
					onClick={() => submitPayment(false)}
					disabled={isSubmitting || loading || dueAmount <= 0}
				>
					{t("Collect Payment")}
				</button>
				<button type="button" className="btn btn-secondary" onClick={toggle} disabled={isSubmitting}>
					{t("Cancel")}
				</button>

			</ModalFooter>
		</Modal>
	);
};

export default CollectPaymentModal;
